// The questionnaire phase. Owns the step index, persists each answer to /public/intake
// (partial saves; best-effort — a failed save never blocks the user), and resumes from
// localStorage. On the final email submit it flushes with completed:true and hands the
// coverage snapshot up to App.
import { useEffect, useRef, useState } from 'react';
import { Shell } from '../components/Shell';
import { Question } from '../components/Question';
import { visibleSteps, timeRemainingLabel, progressFraction, stepKey } from '../lib/flow';
import type { Answers } from '../lib/session';
import { loadAnswers, saveAnswers, loadStep, saveStep, loadSeeded } from '../lib/session';
import { saveIntake, RateLimited, type Coverage, type IntakePayload } from '../lib/api';
import { track } from '../lib/analytics';
import type { Coverage as Cov } from '../lib/api';

// Keys the intake endpoint accepts. `state` is derived client-side; everything else
// maps 1:1 to an answer field.
const INTAKE_KEYS: (keyof IntakePayload)[] = [
  'email', 'bodyPart', 'bodyPartDetail', 'careType', 'surgeryName', 'surgeryDetail', 'surgeryDate',
  'symptomDuration', 'painLevel', 'goal', 'intent', 'paymentPreference',
  'insuranceProvider', 'therapistGenderPreference', 'zip', 'state', 'country', 'emailConsent',
  'disclaimerAccepted',
];

function pickIntake(patch: Answers): IntakePayload {
  const out: IntakePayload = {};
  for (const k of INTAKE_KEYS) {
    if (k in patch && patch[k] !== undefined) (out as Record<string, unknown>)[k] = patch[k];
  }
  return out;
}

export function Questionnaire({ onComplete }: { onComplete: (answers: Answers, coverage: Cov | null) => void }) {
  const [answers, setAnswers] = useState<Answers>(() => loadAnswers());
  const [index, setIndex] = useState<number>(() => Math.min(loadStep(), visibleSteps(loadAnswers()).length - 1));
  const started = useRef(false);

  const steps = visibleSteps(answers);
  const def = steps[index];

  useEffect(() => {
    track('questionnaire_start');
  }, []);

  // One event per question shown, so drop-off can be read question by question
  // (start → complete alone can't say where people leave). Question name only, and
  // ONLY for questions every visitor sees: a branch-only question (back detail,
  // surgery name/date, insurance) appearing in the stream would itself reveal the
  // answer that unlocked it (back pain, post-surgery, paying by insurance). GA4 has
  // no BAA, so branch drop-off is read from the intake table instead.
  const defId = def?.id;
  const onEveryPath = !!def && !def.when;
  useEffect(() => {
    if (defId && onEveryPath) track('questionnaire_step', { funnel_step: stepKey(defId) });
  }, [defId, onEveryPath]);

  // Persist a partial answer. Best-effort; the user is never blocked on the network.
  async function flush(patch: Answers, completed = false) {
    const payload = pickIntake(patch);
    if (Object.keys(payload).length === 0 && !completed) return;
    try {
      await saveIntake({ ...payload, ...(completed ? { completed: true } : {}), lastStep: index });
    } catch (e) {
      if (e instanceof RateLimited) {
        // Soft backoff; a partial save can wait. Retry once.
        setTimeout(() => saveIntake({ ...payload, lastStep: index }).catch(() => {}), e.retryAfterMs);
      }
      // Any other failure is swallowed — answers are safe in localStorage and re-sent
      // on the next step. (A hard failure surfaces at the completing call below.)
    }
  }

  function onAnswer(patch: Answers, opts?: { advance?: boolean }) {
    const next = { ...answers, ...patch };
    setAnswers(next);
    saveAnswers(next);

    if (!started.current) {
      started.current = true;
    }

    const completing = 'completed' in patch;
    if (completing) {
      completeFlow(next);
      return;
    }

    // Fire-and-forget partial save.
    void flush(patch);

    if (opts?.advance) {
      const nextSteps = visibleSteps(next);
      // Skip questions a deep link already answered (e.g. intent from the /learn/ check).
      const seeded = loadSeeded();
      let target = index + 1;
      while (target < nextSteps.length - 1 && seeded.has(nextSteps[target].field) && next[nextSteps[target].field] !== undefined) target++;
      const nextIndex = Math.min(target, nextSteps.length - 1);
      setIndex(nextIndex);
      saveStep(nextIndex);
    }
  }

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function completeFlow(finalAnswers: Answers) {
    setSubmitting(true);
    setError(null);
    let coverage: Coverage | null = null;
    try {
      const res = await saveIntake({ ...pickIntake(finalAnswers), completed: true, lastStep: index });
      coverage = res.coverage;
      track('questionnaire_complete');
    } catch (e) {
      if (e instanceof RateLimited) {
        setError('One sec, finishing up.');
        setTimeout(() => completeFlow(finalAnswers), e.retryAfterMs);
        return;
      }
      // Coverage failed, but we can still show the result screen's empty/generic state.
      setError(null);
    } finally {
      setSubmitting(false);
    }
    onComplete(finalAnswers, coverage);
  }

  if (!def) return null;

  function goBack() {
    const prev = Math.max(0, index - 1);
    setIndex(prev);
    saveStep(prev);
  }

  return (
    <Shell
      progress={progressFraction(answers, index)}
      timeLabel={submitting ? 'Building your plan…' : timeRemainingLabel(answers, index)}
      onBack={goBack}
      showBack={index > 0}
    >
      <Question key={def.id} def={def} answers={answers} onAnswer={onAnswer} />
      {error && <div style={{ marginTop: 14, fontSize: 13, fontWeight: 700, color: 'var(--teal-deep)' }}>{error}</div>}
    </Shell>
  );
}
